import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "@environments/environment";
import { select, Store } from "@ngrx/store";
import { DeepLinkModules, DeepLinkBaseUrls, DeepLinkResourcePaths } from "@shared/constants/deeplink-modules";
import { DeepLinkKpiName, DeepLinkMetadata } from "@shared/models/deeplink.interface";
import { Employee } from "@shared/models/employee.interface";
import { UpdateCustomerEntityGroupAction } from "@store/actions/customerEntityGroupSelect.action";
import { UpdateHistoryStateInfoAction } from "@store/actions/historyStateInfo.action";
import { ApiService } from "@modules/pay-groups/services/api.service";
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, Subject, switchMap, take, tap } from "rxjs";
import { includes } from "lodash";
import { UserRole } from "@shared/constants/roles";
import { User } from "@modules/user-management/_models/user-management-api.interface";
import { ContactPersonService } from "../contact-person/contact-person.service";
import { UserService } from "@modules/user-management/_services/user.service";
import { ToastService } from "../toast/toast.service";
import { CustomerService } from "../customer/customer.service";
import { LegalEntityService } from "../legal-entity/legal-entity.service";
import { PaygroupsService } from "../paygroups/paygroups.service";
import { EmployeeService } from "../employee/employee.service";
import { TabStateService } from "../tab-state/tab-state.service";
import { PayCycleService } from "../pay-cycle/pay-cycle.service";
import { MilestonesService } from "../milestones/milestones.service";
import { Milestone, MilestoneGroupText, PayCycle } from "@shared/models/pay-cycle.interface";
import { PayGroup } from "@shared/models/pay-groups";
import { UpdateSelectedCalendarMonth } from "@store/actions/payCycleSelect.action";
import { UpdateDashboardFilterAction } from "@store/actions/globalDashboardFilterSelect.action";
import { getGlobalDashboardFilterState } from "@store/index";
import { getUserDataState } from "@store/index";
import { FilterMilestoneTypes } from "@modules/global-dashboard/models/global-dashboard-interface";

@Injectable({ providedIn: "root" })
export class DeepLinkService {
	private readonly MILESTONE_TYPE = FilterMilestoneTypes;
	private readonly MILESTONE_GROUP = MilestoneGroupText;
	private readonly STORAGE_KEY = "deepLinkParams";
	private readonly DASHBOARD_ALLOWED_MILESTONE_GROUPS = new Set(["NETS", "TPP1", "TPP2", "TPP3", "TPP4"]);
	private static readonly RELEVANT_DEEP_LINK_PARAM_KEYS = [
		"customerId",
		"legalEntityId",
		"paygroupId",
		"employeeId",
		"paycycleId",
		"payCycleId",
		"milestone",
		"milestoneGroup",
		"payoutStatus",
		"kpiName",
		"originalUrl",
		"previousUrl",
		"module",
		"tab"
	];
	private router = inject(Router);
	private apiService = inject(ApiService);
	private store = inject(Store);
	private contactPersonService = inject(ContactPersonService);
	private userService = inject(UserService);
	private toastService = inject(ToastService);
	private customerService = inject(CustomerService);
	private legalEntityService = inject(LegalEntityService);
	private payGroupService = inject(PaygroupsService);
	private employeeService = inject(EmployeeService);
	private tabStateService = inject(TabStateService);
	private payCycleService = inject(PayCycleService);
	private milestonesService = inject(MilestonesService);

	private customerName = "";

	// ---- GENERATE DEEPLINK -----

	// WPAY_LOCAL <=> CPNOW_LOCAL
	// WPAY_DEV <=> CPNOW_DEV
	// WPAY_QA <=> CPNOW_DEV
	// WPAY_QA2 <=> CPNOW_DEV
	// WPAY_SANDBOX <=> CPNOW_UAT
	// WPAY_PROD <=> CPNOW_PROD

	generateDeeplink(deeplinkParams: DeepLinkMetadata): string {
		let baseUrl: string;
		switch (true) {
			case environment.apiUri.includes("dev"):
				baseUrl = DeepLinkBaseUrls.CPNOW_DEV;
				break;
			case environment.apiUri.includes("qa"):
				baseUrl = DeepLinkBaseUrls.CPNOW_DEV;
				break;
			case environment.apiUri.includes("qa2"):
				baseUrl = DeepLinkBaseUrls.CPNOW_DEV;
				break;
			case environment.apiUri.includes("sandbox"):
				baseUrl = DeepLinkBaseUrls.CPNOW_UAT;
				break;
			case environment.apiUri.includes("prod"):
				baseUrl = DeepLinkBaseUrls.CPNOW_PROD;
				break;
			default:
				baseUrl = DeepLinkBaseUrls.CPNOW_LOCAL;
		}

		let resourcePath: string;

		switch (deeplinkParams.module) {
			case DeepLinkModules.EMPLOYEE_DATA_PAYGROUP_MASTER_DATA:
				resourcePath = DeepLinkResourcePaths.EMPLOYEE_DATA_PAYGROUP_MASTER_DATA;

				break;
			default:
				resourcePath = "";
		}

		// construct deeplink url
		let deeplink = `${baseUrl}${resourcePath}`;

		for (const [key, value] of Object.entries(deeplinkParams))
			if (value !== undefined && value !== null) deeplink += `${key}=${encodeURIComponent(String(value))}&`;

		// remove trailing '&' or '?' if exists
		deeplink = deeplink.replace(/[&?]$/, "");

		return deeplink;
	}

	// ----- DEEPLINK CONSUMPTION -----

	//behaviorSubject to hold deep link tab param
	deeplinkTab$ = new Subject<number | null>();
	pendingEmployee$ = new BehaviorSubject<Employee | null>(null);
	private isProcessingDeepLink = false;

	isHandlingDeepLink(): boolean {
		return this.isProcessingDeepLink;
	}

	preserveParams(params: DeepLinkMetadata): void {
		localStorage.setItem(this.STORAGE_KEY, JSON.stringify(params));
	}

	getStoredParams(): DeepLinkMetadata | null {
		const stored = localStorage.getItem(this.STORAGE_KEY);

		if (!stored) {
			return null;
		}

		try {
			return JSON.parse(stored);
		} catch (error) {
			console.error("Invalid stored deep link params:", error);
			return null;
		}
	}

	clearStoredParams(): void {
		localStorage.removeItem(this.STORAGE_KEY);
	}

	preserveDeepLinkFromUrl(): void {
		const urlParams = new URLSearchParams(window.location.search);

		if (this.hasRelevantParamsFromUrl(urlParams)) {
			const payoutStatus = urlParams.get("payoutStatus") || undefined;
			const kpiName = urlParams.get("kpiName")?.toLowerCase();

			const deepLinkData: DeepLinkMetadata = {
				customerId: urlParams.get("customerId") || undefined,
				legalEntityId: urlParams.get("legalEntityId") || undefined,
				paygroupId: urlParams.get("paygroupId") || undefined,
				employeeId: urlParams.get("employeeId") || undefined,
				paycycleId: urlParams.get("paycycleId") || urlParams.get("payCycleId") || undefined,
				milestone: urlParams.get("milestone") || undefined,
				milestoneGroup: urlParams.get("milestoneGroup") || undefined,
				...(payoutStatus ? { payoutStatus } : {}),
				...(kpiName !== undefined ? { kpiName: kpiName as DeepLinkKpiName } : {}),
				originalUrl: urlParams.get("originalUrl") || urlParams.get("previousUrl") || undefined, // From CPNow or referring system
				timestamp: Date.now(),
				module: (urlParams.get("module") as DeepLinkModules) || undefined,
				tab: Number(urlParams.get("tab")) || undefined
			};

			this.preserveParams(deepLinkData);
		}
	}

	handlePostAuthDeepLink(): void {
		if (this.isProcessingDeepLink) {
			return;
		}

		const stored = this.getStoredParams();
		if (!stored) {
			return;
		}

		this.isProcessingDeepLink = true;
		this.clearStoredParams();

		this.validateDeepLinkParams(stored)
			.pipe(take(1))
			.subscribe(isValid => {
				if (!isValid) {
					this.isProcessingDeepLink = false;
					return;
				}

				this.setStateFromDeepLinkParams(stored);

				// Load employee first (if needed), THEN navigate
				const employeeLoad$ = stored.employeeId ? this.loadAndSetEmployeeAsync(stored.employeeId) : of(null);

				employeeLoad$.pipe(take(1)).subscribe(() => {
					this.userService
						.getUserDetails()
						.pipe(
							take(1),
							catchError(() => {
								// getUserDetails() can error (e.g. /v1/user failure); without this the latch never resets
								this.isProcessingDeepLink = false;
								this.invalidDeeplinkRedirect();
								return of(null);
							})
						)
						.subscribe(user => {
							if (!user) {
								return;
							}

							this.deeplinkTab$.next(stored.tab ?? null);
							this.checkUserPermissions(user, stored);
							this.isProcessingDeepLink = false;
						});
				});
			});
	}

	private loadAndSetEmployeeAsync(employeeId: string): Observable<Employee | null> {
		return this.apiService.getEmployee(employeeId).pipe(
			tap((employee: Employee) => {
				this.pendingEmployee$.next(employee);
			}),
			catchError((error: unknown) => {
				console.error("Error loading employee:", error);
				return of(null);
			})
		);
	}

	setStateFromDeepLinkParams(deepLinkData: DeepLinkMetadata): void {
		// Update customer/entity/paygroup context if available

		if (deepLinkData.customerId || deepLinkData.legalEntityId || deepLinkData.paygroupId) {
			this.store.dispatch(
				new UpdateCustomerEntityGroupAction({
					customerId: deepLinkData.customerId || "",
					customerName: this.customerName,
					legalEntityId: deepLinkData.legalEntityId || "",
					payGroupId: deepLinkData.paygroupId || ""
				})
			);
		}

		// Load employee if employeeId is provided
		if (deepLinkData.employeeId) {
			this.loadAndSetEmployee(deepLinkData.employeeId);
		}
	}

	hasRelevantParams(params: Record<string, unknown>): boolean {
		return DeepLinkService.RELEVANT_DEEP_LINK_PARAM_KEYS.some(key => !!params[key]);
	}

	private hasRelevantParamsFromUrl(urlParams: URLSearchParams): boolean {
		return DeepLinkService.RELEVANT_DEEP_LINK_PARAM_KEYS.some(key => !!urlParams.get(key));
	}

	private loadAndSetEmployee(employeeId: string): void {
		this.apiService.getEmployee(employeeId).subscribe({
			next: (employee: Employee) => {
				// Use the same pattern as the existing service

				this.pendingEmployee$.next(employee);
			},
			error: (error: unknown) => {
				console.error("Error loading employee:", error);
			}
		});
	}

	private checkUserPermissions(user: User, deepLinkData: DeepLinkMetadata): void {
		this.isContactPerson(user, deepLinkData)
			.pipe(take(1))
			.subscribe(isContact => {
				if (includes(user.roles, UserRole.SUPER_ADMIN) || isContact) {
					this.navigateToModule(deepLinkData);
				} else {
					this.toastService.showError("You're not assigned to this particular Paygroup as a Contact Person.");
					this.clearStoredParams();
					this.router.navigate(["/global-dashboard"]);
				}
			});
	}

	private isContactPerson(user: User, deepLinkData: DeepLinkMetadata): Observable<boolean> {
		const objectId = this.resolveObjectId(deepLinkData);
		if (!objectId) return of(false);
		return this.contactPersonService.getContactPersons(objectId).pipe(
			map(res => res.some(contact => contact.userId === user.id)),
			catchError(() => of(false))
		);
	}

	private resolveObjectId(deepLinkData: DeepLinkMetadata): string | null {
		switch (deepLinkData.module) {
			case DeepLinkModules.CUSTOMER_EDIT:
				return deepLinkData.customerId || null;
			case DeepLinkModules.LEGAL_ENTITY_EDIT:
				return deepLinkData.legalEntityId || null;
			case DeepLinkModules.PAYGROUP_EDIT:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.EMPLOYEE_DATA_EMPLOYEE_MASTER_DATA:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.EMPLOYEE_DATA_PAYGROUP_MASTER_DATA:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.LEGAL_ENTITY_LIST:
				return deepLinkData.customerId || null;
			case DeepLinkModules.PAYGROUP_LIST:
				return deepLinkData.customerId || null;
			case DeepLinkModules.GLOBAL_DASHBOARD_FILE_HISTORY:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.GLOBAL_DASHBOARD_PAYOUTS:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.GLOBAL_DASHBOARD_FUNDING_DATE:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.GLOBAL_DASHBOARD_TPP_PAYMENTS:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.GLOBAL_DASHBOARD_TPP_DATA_INPUT:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.GLOBAL_DASHBOARD_CALENDAR:
				return deepLinkData.paygroupId || null;
			case DeepLinkModules.GLOBAL_DASHBOARD_MASTER_DATA:
				return deepLinkData.paygroupId || null;
			default:
				return null;
		}
	}

	private navigateToModule(deepLinkData: DeepLinkMetadata): void {
		// Navigate based on module
		switch (deepLinkData.module) {
			case DeepLinkModules.EMPLOYEE_DATA_EMPLOYEE_MASTER_DATA:
				this.router.navigate(["/employee-data/employee"]);
				break;
			case DeepLinkModules.EMPLOYEE_DATA_PAYGROUP_MASTER_DATA:
				this.router.navigate(["/employee-data/master-data"]);
				break;
			case DeepLinkModules.CUSTOMER_EDIT:
				this.router.navigate(["/customers/edit"]);
				break;
			case DeepLinkModules.LEGAL_ENTITY_EDIT:
				this.router.navigate(["/legal-entities/update"]);
				break;
			case DeepLinkModules.PAYGROUP_EDIT:
				this.router.navigate(["/pay-groups/group"]);
				break;
			case DeepLinkModules.LEGAL_ENTITY_LIST:
				this.router.navigate(["/legal-entities/select"]);
				break;
			case DeepLinkModules.PAYGROUP_LIST:
				this.router.navigate(["/pay-groups/list"]);
				break;
			case DeepLinkModules.GLOBAL_DASHBOARD_FILE_HISTORY:
				//TEST DATA: /global-dashboard?module=global-dashboard-file-history&paygroupId=29595792-0441-460f-a06e-6de8882aa5dd&paycycleId=32c7dfdc-72e5-31f4-be14-fb11053bf221
				this.navigateToFileHistoryModule(deepLinkData);
				break;
			case DeepLinkModules.GLOBAL_DASHBOARD_PAYOUTS:
				//TEST DATA:
				// /global-dashboard?module=global-dashboard-payouts&paygroupId=4c24e6c5-d230-4943-94a4-2f55ee898673&paycycleId=7ce748b8-e61d-3d34-995f-891655bd74a0
				// /global-dashboard?module=global-dashboard-payouts&paygroupId=7f3711d2-ccb5-44f7-8b62-52e8729f7280&paycycleId=700db3ce-629e-3932-8a16-7f8c28f82121&payoutStatus=FAILED_TRANSACTIONS
				this.navigateToPayoutsModule(deepLinkData);
				break;
			case DeepLinkModules.GLOBAL_DASHBOARD_FUNDING_DATE:
				//TEST DATA: /global-dashboard?module=global-dashboard-funding-date&paygroupId=e6c51151-3a2e-4236-882a-20cec5c6fb36&paycycleId=03281067-ac77-3bdf-bed7-ffcd542fb5b2
				this.navigateToFundingDateModule(deepLinkData);
				break;
			case DeepLinkModules.GLOBAL_DASHBOARD_TPP_PAYMENTS:
				//TEST DATA:
				// /global-dashboard?module=global-dashboard-tpp-payments&paygroupId=bbcd9f82-3798-4229-9b5e-bde5ba841b74&paycycleId=96409b6d-fa21-3060-972d-49554780f931
				// /global-dashboard?module=global-dashboard-tpp-payments&paygroupId=db2fead3-8cbd-4d30-ab5b-766471d20e3d&paycycleId=863307cd-781d-399f-aca7-7fbc93dc8963&payoutStatus=FAILED_TRANSACTIONS
				this.navigateToTppPaymentsModule(deepLinkData);
				break;
			case DeepLinkModules.GLOBAL_DASHBOARD_TPP_DATA_INPUT:
				//TEST DATA:
				// /global-dashboard?module=global-dashboard-tpp-data-input&paygroupId=51d955f6-325a-41c2-8335-5922c4401310&paycycleId=44e3d6c2-24f6-3b46-b344-43afd468e22f
				this.navigateToTppDataInputModule(deepLinkData);
				break;
			case DeepLinkModules.GLOBAL_DASHBOARD_CALENDAR:
				this.navigateToDashboardCalendarModule(deepLinkData);
				break;
			case DeepLinkModules.GLOBAL_DASHBOARD_MASTER_DATA:
				//TEST DATA:
				// /global-dashboard?module=global-dashboard-master-data&paygroupId=a813fdf5-92a1-4725-9e8a-e171d2c70bf5&kpiName=failed_beneficiaries
				this.navigateToMasterDataModule(deepLinkData);
				break;
			// Other cases as needed
			default:
				console.warn("Unknown deep link module:", deepLinkData.module);
		}
	}

	private navigateToMasterDataModule(deepLinkData: DeepLinkMetadata): void {
		if (
			!deepLinkData.paygroupId ||
			(deepLinkData.kpiName !== undefined && !this.isValidKpiName(deepLinkData.kpiName))
		) {
			this.invalidDeeplinkRedirect();
			return;
		}

		this.payGroupService
			.getPaygroupWithPaygroupId(deepLinkData.paygroupId)
			.pipe(
				take(1),
				switchMap(payGroup =>
					this.store.pipe(
						select(getGlobalDashboardFilterState),
						take(1),
						map(globalDashboardState => ({ payGroup, globalDashboardState }))
					)
				)
			)
			.subscribe({
				next: ({ payGroup, globalDashboardState }) => {
					if (!globalDashboardState?.globalDashboard) {
						this.invalidDeeplinkRedirect();
						return;
					}

					this.store.dispatch(
						new UpdateCustomerEntityGroupAction({
							customerId: payGroup.customer.id,
							customerName: payGroup.customer.name,
							legalEntityId: payGroup.legalEntityId,
							payGroupId: payGroup.id
						})
					);

					this.store.dispatch(
						new UpdateDashboardFilterAction({
							globalDashboard: {
								...globalDashboardState.globalDashboard,
								kpiSelected: deepLinkData.kpiName ? this.getKpiLabel(deepLinkData.kpiName) : ""
							}
						})
					);

					this.router.navigate(["/global-dashboard/master-data"], { replaceUrl: true });
				},
				error: () => this.invalidDeeplinkRedirect()
			});
	}

	private isValidKpiName(kpiName: string | null | undefined): kpiName is DeepLinkKpiName {
		const normalizedKpiName = kpiName?.toLowerCase();
		return (
			normalizedKpiName === "failed_beneficiaries" ||
			normalizedKpiName === "missing_bank" ||
			normalizedKpiName === "missing_card"
		);
	}

	private getKpiLabel(kpiName: DeepLinkKpiName): string {
		return {
			failed_beneficiaries: "Failed Beneficiaries",
			missing_bank: "Missing Bank Acc.",
			missing_card: "Missing Cards"
		}[kpiName.toLowerCase() as DeepLinkKpiName];
	}

	private navigateToDashboardCalendarModule(deepLinkData: DeepLinkMetadata): void {
		if (!deepLinkData.paygroupId || !deepLinkData.paycycleId) {
			this.invalidDeeplinkRedirect();
			return;
		}

		forkJoin({
			payGroup: this.payGroupService.getPaygroupWithPaygroupId(deepLinkData.paygroupId),
			payCycle: this.payCycleService.getPayCycleById(deepLinkData.paycycleId)
		})
			.pipe(take(1))
			.subscribe({
				next: ({ payGroup, payCycle }) => {
					this.store.dispatch(
						new UpdateCustomerEntityGroupAction({
							customerId: payGroup.customer.id,
							customerName: payGroup.customer.name,
							legalEntityId: payGroup.legalEntityId,
							payGroupId: payGroup.id
						})
					);

					this.store.dispatch(
						new UpdateSelectedCalendarMonth({
							selectedMonth: {
								month: new Date(payCycle.start).getMonth(),
								paygroupId: payGroup.id,
								payCycle
							}
						})
					);

					const normalizedMilestoneGroup = deepLinkData.milestoneGroup?.toUpperCase();
					const expandMilestoneGroupWithName =
						normalizedMilestoneGroup &&
						this.DASHBOARD_ALLOWED_MILESTONE_GROUPS.has(normalizedMilestoneGroup)
							? normalizedMilestoneGroup
							: undefined;

					this.router.navigate(["/global-dashboard/dashboard"], {
						state: {
							expandMilestoneGroupWithName
						},
						replaceUrl: true
					});
				},
				error: () => {
					this.invalidDeeplinkRedirect();
				}
			});
	}

	private navigateToFileHistoryModule(deepLinkData: DeepLinkMetadata): void {
		if (!deepLinkData.paygroupId || !deepLinkData.paycycleId) {
			this.invalidDeeplinkRedirect();
			return;
		}

		forkJoin({
			payGroup: this.payGroupService.getPaygroupWithPaygroupId(deepLinkData.paygroupId),
			payCycle: this.payCycleService.getPayCycleById(deepLinkData.paycycleId),
			milestones: this.milestonesService.getMilestones(deepLinkData.paycycleId, deepLinkData.paygroupId)
		})
			.pipe(take(1))
			.subscribe({
				next: ({ payGroup, payCycle, milestones }) => {
					const milestoneTypeToken = deepLinkData.milestone || this.MILESTONE_TYPE.PAYMENTORDERS;
					const milestone = this.resolveMilestoneFromToken(
						milestoneTypeToken,
						milestones,
						this.MILESTONE_GROUP.NETS
					);

					if (!milestone) {
						this.invalidDeeplinkRedirect();
						return;
					}

					// Dispatch full customer/entity/group context for breadcrumb preservation
					this.store.dispatch(
						new UpdateCustomerEntityGroupAction({
							customerId: payGroup.customer.id,
							customerName: payGroup.customer.name,
							legalEntityId: payGroup.legalEntityId,
							payGroupId: payGroup.id
						})
					);

					this.router.navigate(["/global-dashboard/file-history"], {
						state: this.buildFileHistoryState(payGroup, payCycle, milestone, milestones, deepLinkData),
						replaceUrl: true
					});
				},
				error: () => {
					this.invalidDeeplinkRedirect();
				}
			});
	}

	private navigateToPayoutsModule(deepLinkData: DeepLinkMetadata): void {
		if (!deepLinkData.paygroupId || !deepLinkData.paycycleId) {
			this.invalidDeeplinkRedirect();
			return;
		}

		forkJoin({
			payGroup: this.payGroupService.getPaygroupWithPaygroupId(deepLinkData.paygroupId),
			payCycle: this.payCycleService.getPayCycleById(deepLinkData.paycycleId),
			milestones: this.milestonesService.getMilestones(deepLinkData.paycycleId, deepLinkData.paygroupId)
		})
			.pipe(take(1))
			.subscribe({
				next: ({ payGroup, payCycle, milestones }) => {
					const milestoneTypeToken = deepLinkData.milestone || this.MILESTONE_TYPE.PAYMENTS;
					const milestone =
						this.resolveMilestoneFromToken(milestoneTypeToken, milestones, this.MILESTONE_GROUP.NETS) ||
						milestones.find(
							item =>
								item.group === this.MILESTONE_GROUP.NETS || item.type === this.MILESTONE_TYPE.PAYMENTS
						);
					const hasG2NMilestone = milestones.some(item => item.type === this.MILESTONE_TYPE.PAYMENTORDERS);

					if (!milestone || !hasG2NMilestone) {
						this.invalidDeeplinkRedirect();
						return;
					}

					// Dispatch full customer/entity/group context for breadcrumb preservation
					this.store.dispatch(
						new UpdateCustomerEntityGroupAction({
							customerId: payGroup.customer.id,
							customerName: payGroup.customer.name,
							legalEntityId: payGroup.legalEntityId,
							payGroupId: payGroup.id
						})
					);

					this.router.navigate(["/global-dashboard/payouts"], {
						state: {
							payGroup,
							payCycleId: payCycle.id,
							payCycleName: payCycle.name,
							payCycle: payCycle,
							payrollCycleId: payCycle.payrollCycleId,
							milestones,
							milestone,
							payoutStatusFilter: this.resolveDeepLinkStatusPayment(deepLinkData.payoutStatus)
						},
						replaceUrl: true
					});
				},
				error: () => {
					this.invalidDeeplinkRedirect();
				}
			});
	}

	private navigateToFundingDateModule(deepLinkData: DeepLinkMetadata): void {
		if (!deepLinkData.paygroupId || !deepLinkData.paycycleId) {
			this.invalidDeeplinkRedirect();
			return;
		}

		forkJoin({
			payGroup: this.payGroupService.getPaygroupWithPaygroupId(deepLinkData.paygroupId),
			payCycle: this.payCycleService.getPayCycleById(deepLinkData.paycycleId),
			milestones: this.milestonesService.getMilestones(deepLinkData.paycycleId, deepLinkData.paygroupId)
		})
			.pipe(take(1))
			.subscribe({
				next: ({ payGroup, payCycle, milestones }) => {
					const milestoneTypeToken = deepLinkData.milestone || this.MILESTONE_TYPE.FUNDING;
					const milestone = this.resolveMilestoneFromToken(
						milestoneTypeToken,
						milestones,
						deepLinkData.milestoneGroup!
					);

					if (!milestone) {
						this.invalidDeeplinkRedirect();
						return;
					}

					// Dispatch full customer/entity/group context for breadcrumb preservation
					this.store.dispatch(
						new UpdateCustomerEntityGroupAction({
							customerId: payGroup.customer.id,
							customerName: payGroup.customer.name,
							legalEntityId: payGroup.legalEntityId,
							payGroupId: payGroup.id
						})
					);

					const historyState = this.buildFundingDateHistoryState(payGroup, payCycle, milestones, milestone);
					this.store.dispatch(new UpdateHistoryStateInfoAction(historyState));

					this.router.navigate(["/global-dashboard/funding-date"], {
						state: historyState,
						replaceUrl: true
					});
				},
				error: () => {
					this.invalidDeeplinkRedirect();
				}
			});
	}

	private navigateToTppPaymentsModule(deepLinkData: DeepLinkMetadata): void {
		if (!deepLinkData.paygroupId || !deepLinkData.paycycleId) {
			this.invalidDeeplinkRedirect();
			return;
		}

		forkJoin({
			payGroup: this.payGroupService.getPaygroupWithPaygroupId(deepLinkData.paygroupId),
			payCycle: this.payCycleService.getPayCycleById(deepLinkData.paycycleId),
			milestones: this.milestonesService.getMilestones(deepLinkData.paycycleId, deepLinkData.paygroupId)
		})
			.pipe(take(1))
			.subscribe({
				next: ({ payGroup, payCycle, milestones }) => {
					const milestoneTypeToken = deepLinkData.milestone || this.MILESTONE_TYPE.TPP_PAYMENTS;
					const milestone = this.resolveMilestoneFromToken(
						milestoneTypeToken,
						milestones,
						deepLinkData.milestoneGroup!
					);
					const hasDataInputMilestone = milestones.some(
						item =>
							item.type === this.MILESTONE_TYPE.TPP_PAYMENTORDERS &&
							item.group === deepLinkData.milestoneGroup
					);

					if (!milestone || !hasDataInputMilestone) {
						this.invalidDeeplinkRedirect();
						return;
					}

					// Dispatch full customer/entity/group context for breadcrumb preservation
					this.store.dispatch(
						new UpdateCustomerEntityGroupAction({
							customerId: payGroup.customer.id,
							customerName: payGroup.customer.name,
							legalEntityId: payGroup.legalEntityId,
							payGroupId: payGroup.id
						})
					);

					this.router.navigate(["/global-dashboard/tpp-payments"], {
						state: {
							payGroup,
							payCycle,
							milestones,
							milestone,
							payrollCycleId: payCycle.payrollCycleId,
							payoutStatusFilter: this.resolveDeepLinkStatusPayment(deepLinkData.payoutStatus)
						},
						replaceUrl: true
					});
				},
				error: () => {
					this.invalidDeeplinkRedirect();
				}
			});
	}

	private navigateToTppDataInputModule(deepLinkData: DeepLinkMetadata): void {
		if (!deepLinkData.paygroupId || !deepLinkData.paycycleId) {
			this.invalidDeeplinkRedirect();
			return;
		}

		forkJoin({
			payGroup: this.payGroupService.getPaygroupWithPaygroupId(deepLinkData.paygroupId),
			payCycle: this.payCycleService.getPayCycleById(deepLinkData.paycycleId),
			milestones: this.milestonesService.getMilestones(deepLinkData.paycycleId, deepLinkData.paygroupId)
		})
			.pipe(take(1))
			.subscribe({
				next: ({ payGroup, payCycle, milestones }) => {
					const milestone = this.resolveMilestoneFromToken(
						deepLinkData.milestone || this.MILESTONE_TYPE.TPP_PAYMENTORDERS,
						milestones,
						deepLinkData.milestoneGroup!
					);

					if (!milestone || milestone.type !== this.MILESTONE_TYPE.TPP_PAYMENTORDERS) {
						this.invalidDeeplinkRedirect();
						return;
					}

					this.store.dispatch(
						new UpdateCustomerEntityGroupAction({
							customerId: payGroup.customer.id,
							customerName: payGroup.customer.name,
							legalEntityId: payGroup.legalEntityId,
							payGroupId: payGroup.id
						})
					);

					this.router.navigate(["/global-dashboard/tpp-data-input"], {
						state: { payGroup, payCycle, milestones, milestone, payrollCycleId: payCycle.payrollCycleId },
						replaceUrl: true
					});
				},
				error: () => {
					this.invalidDeeplinkRedirect();
				}
			});
	}

	private resolveMilestoneFromToken(
		milestoneType: string,
		milestones: Milestone[],
		milestoneGroup: string
	): Milestone | undefined {
		const milestoneTypeToken = milestoneType.toUpperCase();

		return milestones.find(
			milestone => milestone.type === milestoneTypeToken && milestone.group === milestoneGroup
		);
	}

	private resolveDeepLinkStatusPayment(payoutStatus?: string): string[] {
		return payoutStatus === "FAILED_TRANSACTIONS"
			? ["FAILED,ATTEMPTS_LIMIT_EXCEEDED", "FAILED_TO_BOOK", "PROCESSING,PENDING"]
			: [];
	}

	private buildFileHistoryState(
		payGroup: PayGroup,
		payCycle: PayCycle,
		milestone: Milestone,
		milestones: Milestone[],
		deepLinkData: DeepLinkMetadata
	): {
		payGroup: PayGroup;
		payCycleId: string;
		payCycleExternalId: string;
		payrollCycleId: string;
		milestone: Milestone;
		milestones: Milestone[];
		payCycle: PayCycle;
		payCycleName: string;
		previousUrl: string;
	} {
		return {
			payGroup,
			payCycleId: payCycle.id,
			payCycleExternalId: payCycle.externalId,
			payrollCycleId: payCycle.payrollCycleId,
			milestone,
			milestones,
			payCycle,
			payCycleName: payCycle.name,
			previousUrl: deepLinkData.originalUrl || "/global-dashboard"
		};
	}

	private buildFundingDateHistoryState(
		payGroup: PayGroup,
		payCycle: PayCycle,
		milestones: Milestone[],
		milestone: Milestone
	): {
		payGroup: PayGroup;
		payCycle: PayCycle;
		payCycleId: string;
		payCycleName: string;
		milestones: Milestone[];
		milestone: Milestone;
		payrollCycleId: string;
	} {
		return {
			payGroup,
			payCycle,
			payCycleId: payCycle.id,
			payCycleName: payCycle.name,
			milestones,
			milestone,
			payrollCycleId: payCycle.payrollCycleId
		};
	}

	// ----- HELPER METHODS -----

	private validateDeepLinkParams(deepLinkData: DeepLinkMetadata): Observable<boolean> {
		// Add validation checks for each relevant deep link parameter
		const checks: Observable<boolean>[] = [];

		if (
			deepLinkData.module === DeepLinkModules.GLOBAL_DASHBOARD_MASTER_DATA &&
			(!deepLinkData.paygroupId ||
				(deepLinkData.kpiName !== undefined && !this.isValidKpiName(deepLinkData.kpiName)))
		) {
			this.invalidDeeplinkRedirect();
			return of(false);
		}

		if (
			deepLinkData.module === DeepLinkModules.GLOBAL_DASHBOARD_FILE_HISTORY ||
			deepLinkData.module === DeepLinkModules.GLOBAL_DASHBOARD_PAYOUTS ||
			deepLinkData.module === DeepLinkModules.GLOBAL_DASHBOARD_FUNDING_DATE ||
			deepLinkData.module === DeepLinkModules.GLOBAL_DASHBOARD_TPP_PAYMENTS ||
			deepLinkData.module === DeepLinkModules.GLOBAL_DASHBOARD_TPP_DATA_INPUT ||
			deepLinkData.module === DeepLinkModules.GLOBAL_DASHBOARD_CALENDAR
		) {
			if (!deepLinkData.paygroupId || !deepLinkData.paycycleId) {
				this.invalidDeeplinkRedirect();
				return of(false);
			}
		}

		if (deepLinkData.customerId) {
			checks.push(
				this.validateParam(
					this.customerService.getCustomer(deepLinkData.customerId),
					"customerId",
					customer => {
						this.customerName = customer.name;
						return !!customer;
					}
				)
			);
		}
		if (deepLinkData.legalEntityId) {
			checks.push(
				this.validateParam(this.legalEntityService.getLegalEntity(deepLinkData.legalEntityId), "legalEntityId")
			);
		}
		if (deepLinkData.paygroupId) {
			checks.push(
				this.validateParam(
					this.payGroupService.getPaygroupWithPaygroupId(deepLinkData.paygroupId),
					"paygroupId"
				)
			);
		}
		if (deepLinkData.employeeId) {
			checks.push(
				this.validateParam(this.employeeService.getEmployeesById(deepLinkData.employeeId), "employeeId")
			);
		}
		if (deepLinkData.paycycleId) {
			checks.push(
				this.validateParam(this.payCycleService.getPayCycleById(deepLinkData.paycycleId), "paycycleId")
			);
		}

		if (!checks.length) {
			this.invalidDeeplinkRedirect();
			return of(false);
		}

		return forkJoin(checks).pipe(map(results => results.every(valid => valid)));
	}

	invalidDeeplinkRedirect(): void {
		this.toastService.showError("Invalid deep link parameters. Redirecting to dashboard.");
		this.tabStateService.setActiveRoute("");
		this.tabStateService.setBreadcrumbIdentifier([]);
		this.clearStoredParams();
		this.navigateToRoleBasedLandingPage();
	}

	private validateParam<T>(
		source$: Observable<T>,
		paramType: string,
		validator?: (data: T) => boolean
	): Observable<boolean> {
		return source$.pipe(
			take(1),
			map(data => {
				const isValid = validator ? validator(data) : !!data;
				if (!isValid) {
					this.validationFailed(paramType);
				}
				return isValid;
			}),
			catchError(() => {
				this.validationFailed(paramType);
				return of(false);
			})
		);
	}

	private validationFailed(paramType: string): void {
		this.toastService.showError(
			`You're not assigned to this particular ${paramType.slice(0, -2)} as a Contact Person.`
		);
		this.tabStateService.setActiveRoute("");
		this.tabStateService.setBreadcrumbIdentifier([]);
		this.clearStoredParams();
		this.navigateToRoleBasedLandingPage();
	}

	private navigateToRoleBasedLandingPage(): void {
		this.store.pipe(select(getUserDataState), take(1)).subscribe(user => {
			const landingPage = user?.roles?.includes(UserRole.CLIENT) ? "/client-landing" : "/global-dashboard";
			this.router.navigate([landingPage]);
		});
	}
}
